import Icon from '@forwardflow/ui/Icons'
import { Avatar } from 'antd'
import React, { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { AccessPermissionItem } from '../AccessPermissionItem'
import cn from 'classnames'
import { ResendInvitationModal } from '@forwardflow/modals/ResendInvitationModal'

interface UserListItemProps {
   name: string
   role: 'Admin' | 'Agent'
   isAdded: boolean
   email: string
   hideActionButton?: boolean
   onSelectionChange: (isSelected: boolean) => void
   isSelected?: boolean
   onPermissionChange?: (
      userId: string,
      permission: 'yes' | 'no' | null
   ) => void
   userId: string
   defaultPermission?: 'yes' | 'no' | null
   showPermissionButtons?: boolean
   isWhitelist?: boolean
   isPending?: boolean
   onDelete?: (email: string) => void
   linkedEmail?: string
}

export const UserListItem: React.FC<UserListItemProps> = ({
   isPending = false,
   name,
   role,
   isAdded,
   email,
   hideActionButton = false,
   onPermissionChange,
   userId,
   defaultPermission = null,
   showPermissionButtons = false,
   isWhitelist = false,
   onDelete,
   linkedEmail,
}) => {
   const [isResendModalOpen, setIsResendModalOpen] = useState(false)
   const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)

   const initials = name
      ?.split(' ')
      ?.map((part) => part?.charAt(0))
      ?.join('')

   const avatarColor = useMemo(() => {
      const hash = name?.split('')?.reduce((acc, char) => {
         return char?.charCodeAt(0) + ((acc << 5) - acc)
      }, 0)

      const colors = [
         '#f56a00',
         '#7265e6',
         '#ffbf00',
         '#00a2ae',
         '#712704',
         '#04B404',
         '#0174DF',
         '#8A0886',
         '#DF0101',
         '#04B4AE',
      ]

      const colorIndex = Math.abs(hash) % colors.length
      return colors[colorIndex]
   }, [name])

   const handlePermissionChange = (permission: 'yes' | 'no' | null) => {
      onPermissionChange?.(userId, permission)
   }

   return (
      <>
         <Link
            to={
               email && !isPending ? `/settings/user-management/${email}` : '#'
            }
            className={cn(
               'flex items-center h-[62px] pl-10 bg-primary/10 overflow-hidden group',
               !isPending &&
                  'hover:bg-primary/20 hover:primary/20 cursor-pointer'
            )}
         >
            <Avatar
               className="w-10 h-10 mr-4 font-semibold shrink-0"
               style={{ backgroundColor: avatarColor }}
            >
               {initials}
            </Avatar>
            <div className="mr-auto min-w-0">
               <p className="font-medium truncate">{name}</p>
               <p className="text-[12px] text-white/50 truncate">{linkedEmail || email}</p>
            </div>
            <div className="flex items-center gap-2 mr-10 shrink-0">
               {role?.toLowerCase() === 'admin' && !isPending && (
                  <p className="text-[11px] text-white/70 py-0.5 px-2 bg-black/20 rounded">
                     {role}
                  </p>
               )}
               {isWhitelist && !isPending && !showPermissionButtons && (
                  <p className="text-[11px] bg-black/20 py-0.5 px-2 rounded">
                     Selected
                  </p>
               )}
            </div>

            {isPending && !showPermissionButtons && (
               <div
                  className="flex items-center gap-3 mr-10 shrink-0"
                  onClick={(e) => {
                     e.preventDefault()
                     e.stopPropagation()
                  }}
               >
                  {showDeleteConfirm ? (
                     <>
                        <span className="text-[12px] text-white/50">
                           Remove invite?
                        </span>
                        <button
                           onClick={() => {
                              onDelete?.(email)
                              setShowDeleteConfirm(false)
                           }}
                           className="text-[12px] text-red-400 hover:text-red-300 transition-colors font-medium"
                        >
                           Yes
                        </button>
                        <button
                           onClick={() => setShowDeleteConfirm(false)}
                           className="text-[12px] text-white/50 hover:text-white/70 transition-colors font-medium"
                        >
                           No
                        </button>
                     </>
                  ) : (
                     <>
                        <button
                           onClick={() => setIsResendModalOpen(true)}
                           className="text-[12px] text-primary hover:text-primary/80 transition-colors font-medium"
                        >
                           Resend
                        </button>
                        {onDelete && (
                           <button
                              onClick={() => setShowDeleteConfirm(true)}
                              className="text-[12px] text-red-400/70 hover:text-red-400 transition-colors font-medium"
                           >
                              Remove
                           </button>
                        )}
                     </>
                  )}
               </div>
            )}

            {showPermissionButtons && (
               <div
                  className="h-full pr-10 flexc"
                  onClick={(e) => {
                     e.preventDefault()
                     e.stopPropagation()
                  }}
               >
                  <AccessPermissionItem
                     onPermissionChange={handlePermissionChange}
                     defaultPermission={defaultPermission}
                  />
               </div>
            )}

            {hideActionButton && (
               <div className="items-center hidden ml-auto font-medium group-hover:hidden">
                  {isAdded ? (
                     <button className="h-10 ml-2  transition-all border rounded w-28 border-[#C11574] bg-[#C11574]/20 hover:bg-[#C11574]/40">
                        Remove
                     </button>
                  ) : (
                     <div className="w-24 gap-1 flexc">
                        <button
                           onClick={(e) => {
                              e.preventDefault()
                              e.stopPropagation()
                           }}
                           className="w-10 h-10 rounded-[2px] flexc bg-black/10 shrink-0"
                        >
                           <Icon name="Delete" className="fill-[#FF5185]" />
                        </button>
                        <Icon name="arrow-down" className="ml-4 -rotate-90" />
                     </div>
                  )}
               </div>
            )}
         </Link>

         <ResendInvitationModal
            isOpen={isResendModalOpen}
            onClose={() => setIsResendModalOpen(false)}
            userEmail={email}
            userName={name}
            role={role}
            linkedEmail={linkedEmail}
         />
      </>
   )
}
